const READINESS_KEY = "prontopsy_device_readiness";
const CALL_READINESS_PREFIX = "prontopsy_call_device_ready_";
const READINESS_MAX_AGE_MS = 4 * 60 * 60 * 1000;
const CALL_READINESS_MAX_AGE_MS = 2 * 60 * 1000;
const CONSULTANT_LOGIN_READINESS_KEY = "prontopsy_consultant_login_readiness";

export function normalizeCallMode(mode) {
  return String(mode || "video").toLowerCase() === "voice" ? "voice" : "video";
}

export function requiredMediaForMode(mode) {
  const normalized = normalizeCallMode(mode);
  return {
    mode: normalized,
    audio: true,
    video: normalized === "video",
  };
}

export function saveDeviceReadiness(result = {}) {
  try {
    localStorage.setItem(
      READINESS_KEY,
      JSON.stringify({
        checkedAt: Date.now(),
        audio: result.audio === true,
        video: result.video === true,
      }),
    );
  } catch (_) {
    /* ignore storage failures */
  }
}

export function hasFreshDeviceReadiness(mode = "video") {
  try {
    const saved = JSON.parse(localStorage.getItem(READINESS_KEY) || "null");
    if (!saved?.checkedAt || Date.now() - saved.checkedAt > READINESS_MAX_AGE_MS) {
      return false;
    }
    const required = requiredMediaForMode(mode);
    return saved.audio === true && (!required.video || saved.video === true);
  } catch (_) {
    return false;
  }
}

async function permissionState(name) {
  try {
    if (!navigator.permissions?.query) return "unsupported";
    const result = await navigator.permissions.query({ name });
    return result?.state || "unsupported";
  } catch (_) {
    return "unsupported";
  }
}

// Validate a cached grant without calling getUserMedia. This keeps incoming-call
// acceptance instant while detecting revoked permission or unplugged devices.
export async function hasUsableFreshDeviceReadiness(mode = "video") {
  if (!hasFreshDeviceReadiness(mode)) return false;
  const required = requiredMediaForMode(mode);
  const [microphonePermission, cameraPermission] = await Promise.all([
    permissionState("microphone"),
    required.video ? permissionState("camera") : Promise.resolve("granted"),
  ]);
  if (microphonePermission === "denied" || microphonePermission === "prompt") return false;
  if (required.video && (cameraPermission === "denied" || cameraPermission === "prompt")) return false;

  try {
    if (!navigator.mediaDevices?.enumerateDevices) return true;
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasMicrophone = devices.some((device) => device.kind === "audioinput");
    const hasCamera = devices.some((device) => device.kind === "videoinput");
    return hasMicrophone && (!required.video || hasCamera);
  } catch (_) {
    return false;
  }
}

const LOGIN_PROBE_KEY = "prontopsy_login_device_probe";

export function profileOffersModes(profile) {
  const modes = { chat: true, voice: false, video: false };
  const rows = []
    .concat(profile?.service || [])
    .concat(profile?.serviceDataTableMap || []);
  const visit = (slug) => {
    const s = String(slug || "").toLowerCase();
    if (s === "voice" || s === "audio") modes.voice = true;
    if (s === "video") modes.video = true;
    if (s === "chat") modes.chat = true;
  };
  rows.forEach((row) => {
    const modeField = row?.mode;
    if (Array.isArray(modeField)) {
      modeField.forEach((m) => visit(m?.slug || m?.title?.en || m?.title || m));
    } else if (modeField) {
      visit(modeField?.slug || modeField?.title?.en || modeField);
    }
    visit(row?.service?.mode?.slug);
  });
  return modes;
}

export async function probeDevices({ audio = false, video = false } = {}) {
  const result = { audio: false, video: false };
  if (!audio && !video) return result;
  if (!navigator.mediaDevices?.getUserMedia) return result;
  const stop = (stream) => {
    try {
      stream?.getTracks?.().forEach((t) => t.stop());
    } catch (_) {
      /* ignore */
    }
  };
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: !!audio,
      video: !!video,
    });
    result.audio = !audio || stream.getAudioTracks().some((t) => t.readyState === "live");
    result.video = !video || stream.getVideoTracks().some((t) => t.readyState === "live");
    stop(stream);
  } catch (_) {
    if (audio) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        result.audio = stream.getAudioTracks().some((t) => t.readyState === "live");
        stop(stream);
      } catch (__) {
        /* mic also denied */
      }
    }
  }
  return result;
}

export function saveLoginDeviceProbe(result) {
  try {
    sessionStorage.setItem(
      LOGIN_PROBE_KEY,
      JSON.stringify({ ...result, at: Date.now() }),
    );
  } catch (_) {
    /* ignore */
  }
}

export function takeLoginDeviceProbe() {
  try {
    const raw = sessionStorage.getItem(LOGIN_PROBE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(LOGIN_PROBE_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - parsed.at > 5 * 60 * 1000) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export function setConsultantLoginReadinessPending(pending) {
  try {
    if (pending) sessionStorage.setItem(CONSULTANT_LOGIN_READINESS_KEY, "pending");
    else sessionStorage.removeItem(CONSULTANT_LOGIN_READINESS_KEY);
  } catch (_) {
    /* ignore storage failures */
  }
}

export function isConsultantLoginReadinessPending() {
  try {
    return sessionStorage.getItem(CONSULTANT_LOGIN_READINESS_KEY) === "pending";
  } catch (_) {
    return false;
  }
}

export function clearDeviceReadiness() {
  try {
    localStorage.removeItem(READINESS_KEY);
  } catch (_) {
    /* ignore storage failures */
  }
}

export function saveCallDeviceReadiness(appointmentId, result = {}) {
  if (!appointmentId) return;
  try {
    sessionStorage.setItem(
      `${CALL_READINESS_PREFIX}${appointmentId}`,
      JSON.stringify({
        checkedAt: Date.now(),
        audio: result.audio === true,
        video: result.video === true,
      }),
    );
  } catch (_) {
    /* ignore storage failures */
  }
}

export function hasFreshCallDeviceReadiness(appointmentId, mode = "video") {
  if (!appointmentId) return false;
  try {
    const saved = JSON.parse(
      sessionStorage.getItem(`${CALL_READINESS_PREFIX}${appointmentId}`) || "null",
    );
    if (!saved?.checkedAt || Date.now() - saved.checkedAt > CALL_READINESS_MAX_AGE_MS) {
      return false;
    }
    const required = requiredMediaForMode(mode);
    return saved.audio === true && (!required.video || saved.video === true);
  } catch (_) {
    return false;
  }
}
